// SessionExpiryWatcher — single SPA-level component that listens for
// `dispatchSessionExpired()` events (fired by api-client on any non-
// auth 401) and decides how to recover the user.
//
// Behaviour by current route:
//   /dashboard/*  — clear local session state + navigate to
//                   /login?return_to=<current>&reason=session_expired
//                   so LoginPage can both deep-link back after re-auth
//                   AND show a friendly "您的登入已逾時" toast.
//   /liff/*       — no redirect (yet); LIFF pages manage their own
//                   auth via liff.getIDToken() re-exchange. A future
//                   PR can wire a refresh attempt here.
//   /scan/*, /enroll-*, /pair, /verify-email, /, /login, etc.
//                — no redirect. These are public surfaces (CMAC /
//                  WebAuthn / single-use token auth) that don't share
//                  the dashboard session. A 401 from them is a real
//                  caller error, not a session expiry.
//
// Also wires the `visibilitychange` listener that calls /v1/auth/me
// when the tab regains focus. If `me` returns null and we previously
// thought we had a dashboard session, that's a delayed expiry — fire
// the same event so the user sees the redirect on focus rather than
// on their next click.

import React, { useEffect } from "react";
import { onSessionExpired, dispatchSessionExpired } from "./session-expiry.js";
import { fetchMe, hasSessionHint } from "./auth.js";
import { clearLocalState } from "../dashboard/lib/logout.js";

export default function SessionExpiryWatcher(): React.ReactElement | null {
  useEffect(() => {
    const off = onSessionExpired(() => {
      if (typeof window === "undefined") return;
      const path = window.location.pathname;
      if (path.startsWith("/dashboard")) {
        clearLocalState();
        const returnTo = encodeURIComponent(path + window.location.search);
        window.location.assign(`/login?return_to=${returnTo}&reason=session_expired`);
        return;
      }
      // LIFF / scan / public — handled by the page itself for now.
    });

    const onVisible = (): void => {
      if (typeof document === "undefined") return;
      if (document.visibilityState !== "visible") return;
      if (typeof window === "undefined") return;
      // Only bother re-validating on dashboard routes — LIFF / scan /
      // public surfaces have their own session shape.
      if (!window.location.pathname.startsWith("/dashboard")) return;
      // Don't probe if we never had a hint of a session in the first
      // place; that just spams the API with 401s.
      if (!hasSessionHint()) return;
      void fetchMe().then((me) => {
        if (!me) {
          // Server says we're logged out. Fire the standard event so
          // the same redirect path is taken whether expiry was caught
          // by an API call or this background probe.
          dispatchSessionExpired();
        }
      }).catch(() => {
        // Network blip — leave the user alone; next API call will tell
        // us for real.
      });
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => {
      off();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return null;
}
